import { lazy, Suspense, useCallback, useEffect, useTransition } from 'react'

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { z } from 'zod'

import { PanelForm } from '@/features/actions/components/form'
import { actionQueries, useActionList } from '@/features/actions/queries'
import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import {
	Section,
	SectionDescription,
	SectionTitle,
} from '@/features/app-shell/section'

import { ROLES } from '@/shared/auth/constants'
import { getCurrentTenant } from '@/shared/auth/store'
import { roleAuthGuard } from '@/shared/auth/utils/router'
import { AttachAutomationPanel } from '@/shared/automation-attach/composer'
import { TableFilterList } from '@/shared/data-table/components/table-filter-list'
import { TableFullSkeleton } from '@/shared/data-table/components/table-skeleton'
import { usePanelActions } from '@/shared/panel/store'

import { ErrorBoundary } from '@/components/common/error-boundary'

import type { Action } from '@/features/actions/types'

const ActionsToolbar = lazy(
	() => import('@/features/actions/components/table/toolbar'),
)
const ActionsTable = lazy(() => import('@/features/actions/components/table'))

const searchSchema = z.object({
	q: z.string().optional(),
	action: z.string().optional(),
	settings: z.string().optional(),
	filters: z
		.object({
			vendor: z.string().optional(),
			category: z.string().optional(),
		})
		.optional(),
})

export const Route = createFileRoute('/_dashboard/actions/')({
	validateSearch: zodValidator(searchSchema),

	loaderDeps: ({ search }) => search,

	// Only Analysts can access this route
	beforeLoad: () => roleAuthGuard(ROLES.Analyst),

	// Make sure the actions are fetched
	loader: ({ context: { queryClient }, deps: search }) => {
		const tenant = getCurrentTenant()

		if (tenant) {
			queryClient.ensureQueryData({
				...actionQueries.list(tenant, search),
				revalidateIfStale: true,
			})
		}
	},

	head: () => ({
		meta: [
			{
				title: 'Actions | BitLyft AIR®',
			},
		],
	}),

	component: ResponseActionsRoute,
	pendingComponent: ResponseActionsRouteSkeleton,
	errorComponent: RouteErrorComponent,
})

function ResponseActionsRoute() {
	const navigate = useNavigate()
	const search = Route.useSearch()
	const { createPanel, close } = usePanelActions()

	const { data: actions, dataUpdatedAt, refetch } = useActionList()

	const [_isPending, startTransition] = useTransition()

	/**
	 * Find action by ID from the loaded actions list
	 */
	const findAction = useCallback(
		(actionId: string) =>
			actions.find((action) => action.action_id === actionId) as Action,
		[actions],
	)

	/**
	 * PANEL CONTROLS
	 */

	// Handles the selection of a module and updates the URL
	const handleSelectModule = (module?: Action, type?: 'run' | 'settings') => {
		if (!module) return

		// Set the panel
		if (type === 'run') {
			createPanel({
				title: `Run: ${module.friendly_name}`,
				description: module.description,
				render: () => <PanelForm module={module} handleClose={close} />,
				onClose: handleClose,
			})

			startTransition(() => {
				navigate({
					to: '.',
					search: (prev) => ({ ...prev, action: module.action_id }),
				})
			})
		}

		if (type === 'settings') {
			createPanel({
				title: `Settings: ${module.friendly_name}`,
				description: module.description,
				render: () => (
					<AttachAutomationPanel
						automationName={module.action_id}
						automationType="action"
					/>
				),
				onClose: handleClose,
			})

			startTransition(() => {
				navigate({
					to: '.',
					search: (prev) => ({ ...prev, settings: module.action_id }),
				})
			})
		}
	}

	// Handles the closing of the panel and updates the URL
	const handleClose = () => {
		startTransition(() => {
			navigate({
				to: '.',
				search: (prev) => ({ ...prev, action: undefined }),
			})
		})
	}

	/**
	 * Side effect: When URL has action param, trigger module selection
	 */
	useEffect(() => {
		if (search.action) {
			handleSelectModule(findAction(search.action), 'run')
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps, @eslint-react/exhaustive-deps
	}, [search.action])

	return (
		<Section>
			<SectionTitle>Actions</SectionTitle>
			<SectionDescription>
				Individual automated actions that can be run manually or mapped to
				policies for automated response.
			</SectionDescription>

			<div className="flex flex-col gap-4">
				<Suspense
					fallback={<TableFullSkeleton rows={10} withFilters withLastUpdated />}
				>
					<ActionsToolbar updatedAt={dataUpdatedAt} refetch={refetch} />

					<TableFilterList />

					<ErrorBoundary>
						<ActionsTable onActionSelect={handleSelectModule} />
					</ErrorBoundary>
				</Suspense>
			</div>
		</Section>
	)
}

/**
 * Loading state for Response Actions
 */
function ResponseActionsRouteSkeleton() {
	return (
		<Section>
			<SectionTitle>Actions</SectionTitle>
			<SectionDescription>
				Individual automated actions that can be run manually or mapped to
				policies for automated response.
			</SectionDescription>

			<TableFullSkeleton rows={10} withFilters withLastUpdated />
		</Section>
	)
}
